import type { SeriesSlots } from './tokens';

/**
 * The categorical series: six hues for telling kinds of thing apart at a
 * glance — today the overview calendar's six date kinds, in their order
 * (order by, street date, ships, arriving, delivered, received).
 *
 * Tones say what state something is in; series say which kind it is, so the
 * two never share a family. No series sits near danger red (the calendar marks
 * lateness with it) and the one neutral slot is a warm stone, not the slate
 * of the UI chrome, so it cannot pass for a pale blue.
 *
 * Unlike tones, each slot sets its own wash lightness and chroma: a pale wash
 * near white has little chroma to spend, and at one shared lightness violet
 * and blue washes land within a few ΔE of each other. Stepping each slot to
 * where its hue has room keeps every pair of washes apart (min ΔE ~5, OKLab
 * ×100) and every pair of inks apart (min ~10), with ink on wash >= 4.5:1.
 * Re-measure all pairs if a slot changes.
 */
interface SeriesSpec {
  hue: number;
  /** Wash lightness and chroma; chroma kept inside sRGB at that lightness. */
  surfaceL: number;
  surfaceC: number;
  inkL: number;
  inkC: number;
  inkHue?: number;
}

const SERIES_SPEC: SeriesSpec[] = [
  { hue: 75,  surfaceL: 0.930, surfaceC: 0.058, inkL: 0.50, inkC: 0.110, inkHue: 60 }, // amber
  { hue: 305, surfaceL: 0.905, surfaceC: 0.055, inkL: 0.47, inkC: 0.170 },             // violet
  { hue: 80,  surfaceL: 0.930, surfaceC: 0.010, inkL: 0.47, inkC: 0.018 },             // stone
  { hue: 248, surfaceL: 0.900, surfaceC: 0.048, inkL: 0.47, inkC: 0.150, inkHue: 255 }, // blue
  { hue: 190, surfaceL: 0.935, surfaceC: 0.052, inkL: 0.51, inkC: 0.085, inkHue: 195 }, // teal
  { hue: 135, surfaceL: 0.930, surfaceC: 0.065, inkL: 0.49, inkC: 0.130 },             // green
];

const round = (n: number) => Number(n.toFixed(3));

export function buildSeries(opts: { mode: 'light' | 'dark'; groundL?: number }): SeriesSlots[] {
  return SERIES_SPEC.map((s) => {
    const inkHue = s.inkHue ?? s.hue;
    if (opts.mode === 'dark') {
      // Deep washes a fixed step above the ground, light inks: the same
      // hue and relative chroma, so a kind reads as itself in both modes.
      const g = opts.groundL ?? 0.25;
      return {
        surface: `oklch(${round(g + 0.095)} ${round(s.surfaceC * 0.95)} ${s.hue})`,
        ink: `oklch(0.83 ${round(s.inkC * 0.62)} ${inkHue})`,
        line: `oklch(${round(g + 0.2)} ${round(s.surfaceC * 1.2)} ${s.hue})`,
      };
    }
    return {
      surface: `oklch(${s.surfaceL} ${s.surfaceC} ${s.hue})`,
      ink: `oklch(${s.inkL} ${s.inkC} ${inkHue})`,
      line: `oklch(${round(s.surfaceL - 0.075)} ${round(s.surfaceC * 1.5)} ${s.hue})`,
    };
  });
}
